import { memo } from "react";
import { Star } from "@phosphor-icons/react";
import { RATING_LABELS } from "shared/types/candidate-follow-up";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface StarRatingProps {
  value: number | null;
  onChange?: (value: number | null) => void;
  readonly?: boolean;
  size?: number;
}

// TooltipProvider is defined once outside the component tree.
// Placing it inside the render function caused it to remount on every render,
// forcing all 5 Tooltip instances to rebuild and re-register their event listeners.
const StarRating = memo(({
  value,
  onChange,
  readonly = false,
  size = 16,
}: StarRatingProps) => {
  const handleClick = (star: number) => {
    if (readonly || !onChange) return;
    // Clicking the same star clears the rating
    onChange(value === star ? null : star);
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Tooltip key={star}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClick(star);
              }}
              disabled={readonly}
              className={`transition-colors ${
                readonly ? "cursor-default" : "cursor-pointer hover:scale-110"
              }`}
            >
              <Star
                size={size}
                weight={value !== null && star <= value ? "fill" : "regular"}
                className={
                  value !== null && star <= value
                    ? "text-amber-400"
                    : "text-neutral-300"
                }
              />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{RATING_LABELS[star]}</p>
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
});

StarRating.displayName = "StarRating";

// Wrap with a single shared TooltipProvider so the context is never recreated.
const StarRatingWithProvider = (props: StarRatingProps) => (
  <TooltipProvider>
    <StarRating {...props} />
  </TooltipProvider>
);

export default StarRatingWithProvider;
